import { FC, useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useNotices } from '@/features/notices/useNotices';
import {
  getPartialForm,
  isAllowedCollection,
  PartialFormAllowedCollection,
} from '@/shared/lib/hooks/getPartialForm';
import { toDto } from '@/shared/lib/hooks/use-form-render/format-functions';
import { useFormRenderV2 } from '@/shared/lib/hooks/useFormRenderV2';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { isDtoId } from '@/shared/utils/is';
import { BtnClose } from '@/widgets/modals/components/BtnClose';

import { isColDto } from '../../helpers';
import { ItemBaseDto, UpdateCollectionsModalCollectionItem } from '../../types';

import {
  Actions,
  Body,
  Container,
  Field,
  Fields,
  GeneralInfo,
  Header,
  Label,
  StyledDrawer,
  Value,
} from './styled';
import { useFields } from './useFields';

type UpdateItemProps = {
  item: UpdateCollectionsModalCollectionItem;
  filledData: ItemBaseDto | undefined;
  onChange: (data: UpdateCollectionsModalCollectionItem) => void;
  onCancel: () => void;
};

const getPartialFormParams = (
  dto: ItemBaseDto,
): {
  type: string;
  collectionName: PartialFormAllowedCollection;
} | null => {
  if (isColDto(CollectionName.ASSETS, dto)) {
    return { collectionName: CollectionName.ASSETS, type: dto.category };
  }
  if (isColDto(CollectionName.PRIVATE_DOCUMENTS, dto)) {
    return { collectionName: CollectionName.ASSETS, type: dto.privateCategory };
  }
  if (isColDto(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, dto)) {
    return { collectionName: CollectionName.ASSETS, type: dto.type };
  }
  if (isColDto(CollectionName.CONTACTS, dto)) {
    return { collectionName: CollectionName.ASSETS, type: dto.type };
  }
  if (isColDto(CollectionName.BENEFICIARIES, dto)) {
    return { collectionName: CollectionName.ASSETS, type: dto.group };
  }

  return null;
};

const getTitle = (item: ItemBaseDto) => {
  if (isColDto(CollectionName.ASSETS, item)) {
    return item.nickName ?? item.nickname;
  }
  if (isColDto(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, item)) {
    return item.name;
  }
  if (isColDto(CollectionName.PRIVATE_DOCUMENTS, item)) {
    return item.name;
  }
  if (isColDto(CollectionName.BENEFICIARIES, item)) {
    return item.nickName;
  }
  if (isColDto(CollectionName.CONTACTS, item)) {
    return item.nickname;
  }

  return undefined;
};

export const UpdateItem: FC<UpdateItemProps> = ({ item, filledData, onChange, onCancel }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'updateCollections' });
  const { t: tc } = useTranslation('common');
  const formId = useId();
  const infoFields = useFields(item);
  const notices = useNotices();

  const sectionItems = useMemo(() => {
    const collectionName = isDtoId(item.data.id);

    if (!collectionName) {
      return {};
    }

    if (!isAllowedCollection(collectionName)) {
      return {};
    }

    const partialFormParams = getPartialFormParams(item.data);

    if (!partialFormParams) {
      return {};
    }

    const partialForm = getPartialForm({
      ...partialFormParams,
      customFields: item.updateFields,
      withFastAddFields: false,
      withRequiredFields: false,
    });

    return partialForm ?? {};
  }, [item]);

  const form = useForm<Omit<BaseDto, 'id'>>({
    defaultValues: { ...filledData },
    mode: 'onChange',
  });

  const { Component } = useFormRenderV2({
    sectionItems,
    form,
  });

  const onSubmit = (data: Omit<BaseDto, 'id'>) => {
    const dtoData = toDto(data);

    onChange({ data: { ...item.data, ...dtoData }, updateFields: item.updateFields });
    onCancel();
  };

  const onError = () => notices.error(tc('formValidationError'));

  return (
    <StyledDrawer open={true} closable={false} footer={null} width={600} onClose={onCancel}>
      <Container>
        <Header>
          <Title size={22} bold>
            {getTitle(item.data)}
          </Title>
          <BtnClose onClick={onCancel} />
        </Header>
        <Body className="uikitScrollbar">
          <GeneralInfo>
            <Title size={18} bold>
              {t('infoTitle')}
            </Title>
            <Fields>
              {infoFields.map((iField, index) => (
                <Field key={index}>
                  <Label>{iField.label}</Label>
                  <Value>{iField.value}</Value>
                </Field>
              ))}
            </Fields>
          </GeneralInfo>
          <GeneralInfo>
            <Title size={18} bold>
              {t('fieldsTitle')}
            </Title>
            <form id={formId} onSubmit={form.handleSubmit(onSubmit, onError)}>
              {Component}
            </form>
          </GeneralInfo>
        </Body>
        <Actions>
          <Button htmlType="button" type="text" onClick={onCancel}>
            {t('btnCancel')}
          </Button>
          <Button form={formId} htmlType="submit" style={{ width: 200 }}>
            {t('btnSave')}
          </Button>
        </Actions>
      </Container>
    </StyledDrawer>
  );
};
